<template>
  <div
    class="customer-overview"
    :class="{ 'has-tabs': creditInsightsTabsEnabled }"
  >
    <div
      v-if="customerLoading"
      id="customerLoading"
      key="customer-loading"
      class="d-flex align-items-center h-100 justify-content-center"
    >
      <i-spinner size="xl"></i-spinner>
    </div>
    <div
      v-else-if="!selectedCustomer"
      id="customer-error"
      key="customer-error"
      data-testid="customer-error"
      class="d-flex align-items-center px-5 text-center flex-column h-100 justify-content-center"
    >
      <h4>Something went wrong!</h4>
      <p>
        You might not have permission to view this customer, or it might have
        been deleted.
        <br />
        Try reloading the page.
      </p>
    </div>
    <div v-else key="customer-details" class="ui-wrapper d-flex flex-row">
      <div class="documents-wrapper flex-grow-1 position-relative">
        <document-list
          class="customer-documents flex-grow-1"
          :selected-customer="selectedCustomer"
          :show-upload-button="showUploadButton"
        />
      </div>
      <div v-if="canViewCustomerReviewAgent" class="details-sidebar">
        <customer-review-agent-report />
      </div>
      <div v-else class="details-sidebar">
        <div class="customer-details" :class="{ 'h-100': !hasParsedDetails }">
          <customer-summary :selected-customer="selectedCustomer" />
        </div>

        <div v-if="hasParsedDetails" class="customer-details">
          <customer-parsed-details-list
            :customer="selectedCustomer"
            :show-blocklist-suggestions="showBlocklistSuggestions"
          />
        </div>
      </div>
    </div>
    <router-view v-if="selectedCustomer" :key="routeKey" />
  </div>
</template>
<script>
import { iSpinner } from '@inscribe/signal';
import { mapGetters, mapState } from 'vuex';
import DocumentList from '@/V2/documents/document-list/DocumentList';
import CustomerReviewAgentReport from '@/V2/customers/customer-review-agent/CustomerReviewAgentReport';
import CustomerSummary from '@/V2/customers/CustomerSummary';
import CustomerParsedDetailsList from '@/V2/customers/CustomerParsedDetailsList';

export default {
  components: {
    iSpinner,
    'customer-parsed-details-list': CustomerParsedDetailsList,
    'customer-summary': CustomerSummary,
    'document-list': DocumentList,
    CustomerReviewAgentReport,
  },
  beforeRouteLeave(to, from, next) {
    const isUploading = this.someDocumentUploading();

    if (
      isUploading &&
      !window.confirm(
        'Some documents are still uploading. Are you sure you want to leave?',
      )
    ) {
      next(false);
    } else {
      next();
    }
  },
  props: {
    isPdfExportEnabled: {
      type: Boolean,
      default: false,
    },
    isCompletePdfExport: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      creditInsightsTabsEnabled: 'creditInsightsTabsEnabled',
      documentsListEmpty: 'documentsListEmpty',
      someDocumentUploading: 'someDocumentUploading',
      canViewCustomerReviewAgent: 'canViewCustomerReviewAgent',
    }),
    ...mapState({
      currentUser: (state) => state.currentUser,
      documentsLoading: (state) => state.documentsLoading,
      selectedCustomer: (state) => state.customers.selectedCustomer,
      customerLoading: (state) => state.customers.customerLoading,
      showBlocklistSuggestions: (state) =>
        state.customers.showBlocklistSuggestions,
    }),
    routeKey() {
      return this.$route.params.customer_id + this.$route.params.document_id;
    },
    showUploadButton() {
      return (
        this.selectedCustomer &&
        this.selectedCustomer.organisation?.id ===
          this.currentUser.organisation?.id
      );
    },
    hasParsedDetails() {
      return (
        !this.documentsLoading &&
        this.selectedCustomer?.parsedDetails?.length > 0
      );
    },
  },
  mounted() {
    document.body.classList.add('redesign-customer-view-body');
  },
  destroyed() {
    document.body.classList.remove('redesign-customer-view-body');
  },
};
</script>

<style lang="scss" scoped>
$header-bar-height: 73px;
$header-bar-height-with-tabs: 120px;
$pagination-bar-height: 60px;

.customer-overview {
  .flex-column {
    display: flex;
    flex-direction: column;
  }

  &.has-tabs {
    .ui-wrapper {
      .documents-wrapper {
        height: calc(100vh - #{$header-bar-height-with-tabs});

        &.pagination {
          height: calc(
            100vh - #{$header-bar-height-with-tabs} - #{$pagination-bar-height}
          );
        }
      }

      .details-sidebar {
        height: calc(100vh - #{$header-bar-height-with-tabs});
      }
    }
  }

  .ui-wrapper {
    width: 100%;

    .documents-wrapper {
      overflow-y: auto;
      position: relative;
      height: calc(100vh - #{$header-bar-height});
      padding: 32px;
      background: $light-neutral-default;

      &.pagination {
        height: calc(100vh - #{$header-bar-height} - #{$pagination-bar-height});
      }

      .customer-documents {
        max-width: 1200px;
        margin: 0 auto;
        height: 100%;

        :deep(.document-list__documents) {
          height: 100%;

          > div,
          .upload-wrapper,
          #dropzone {
            height: 100%;
          }
        }

        .empty-state {
          img {
            max-width: 150px;
          }
        }

        .upload-wrapper {
          overflow: auto;

          #dropzone {
            display: flex;
            max-height: 100%;
          }

          .dz-message {
            width: 100%;
            overflow: hidden;

            .dragging {
              min-height: 450px;
            }

            & > div {
              max-height: 100%;
              overflow: auto;
            }
          }
        }
      }
    }

    .details-sidebar {
      flex-shrink: 0;
      background-color: $color-white;
      border-left: 1px solid $color-navy-blue-20;
      overflow: hidden;
      position: relative;
      height: calc(100vh - #{$header-bar-height});

      .customer-details {
        @extend .flex-column;

        font-size: $font-size-secondary;
        max-width: 225px;
        width: calc(100vw - #{$padding-container * 2});
        height: 50%;
        overflow: auto;

        &:first-child {
          border-bottom: 1px solid $color-navy-blue-20;
        }
      }
    }
  }
}
</style>
